import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions, maskitoTransform } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { phoneMask } from '../../constants/maskito.constants';
import { GenericComponent } from '../../misc/components/generic/generic.component';
import { CustomValidators } from '../../misc/validators/custom-validators';
import * as fromUserActions from '../../store/actions/user.actions';
import * as fromUserSelectors from '../../store/selectors/user.selectors';

interface RegisterFormGroup {
  company: FormControl<string | null>;
  confirmEmail: FormControl<string | null>;
  email: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  phone: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-register',
  standalone: false,
  templateUrl: './register.page.html'
})
export class RegisterPageComponent extends GenericComponent {
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);

  registerForm: FormGroup<RegisterFormGroup> = new FormGroup<RegisterFormGroup>(
    {
      company: new FormControl(null, Validators.required),
      confirmEmail: new FormControl(null, [Validators.required, Validators.email, Validators.maxLength(254)]),
      email: new FormControl(null, [Validators.required, Validators.email, Validators.maxLength(254)]),
      firstName: new FormControl(null, Validators.required),
      lastName: new FormControl(null, Validators.required),
      phone: new FormControl(null, Validators.required)
    },
    {
      validators: [CustomValidators.confirmEmail()]
    }
  );

  readonly phoneMask: MaskitoOptions = phoneMask;
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();

  ionViewDidEnter() {
    this.registerForm.reset();
    this.addUniqueSubscription(
      'selectUserDetails',
      this.store.pipe(select(fromUserSelectors.selectDetails)).subscribe((userDetails) => {
        this.registerForm.setValue({
          company: userDetails.company || '',
          confirmEmail: userDetails.email || '',
          email: userDetails.email || '',
          firstName: userDetails.firstName || '',
          lastName: userDetails.lastName || '',
          phone: maskitoTransform(userDetails.phone ?? '', this.phoneMask)
        });
        this.registerForm.updateValueAndValidity();
        this.changeDetectorRef.detectChanges();
      })
    );
  }

  onRegister() {
    this.store.dispatch(
      fromUserActions.registerMobileUser({
        payload: {
          data: {
            company: this.registerForm.get('company')?.value || '',
            email: this.registerForm.get('email')?.value || '',
            firstName: this.registerForm.get('firstName')?.value || '',
            lastName: this.registerForm.get('lastName')?.value || '',
            phone: (this.registerForm.get('phone')?.value || '').replace(/\D/g, '')
          },
          isOnboardingFlow: true
        }
      })
    );
  }

  onFirstNameInput(event: CustomEvent) {
    if (event.detail.value === 'rectangular') {
      this.registerForm.setValue({
        company: 'Big Time Properties',
        confirmEmail: 'support@redshedtechnology.com',
        email: 'support@redshedtechnology.com',
        firstName: 'Joe',
        lastName: 'Realtor',
        phone: maskitoTransform('5555555555', this.phoneMask)
      });
      this.registerForm.updateValueAndValidity();
      this.changeDetectorRef.detectChanges();
    }
  }
}
